import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const VISITOR_ID_KEY = "neosdr_visitor_id";
const SESSION_ID_KEY = "neosdr_session_id";
const PAGEVIEW_ID_KEY = "neosdr_pageview_id";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Gera um ID único para o visitante
function generateVisitorId(): string {
  return "v_" + crypto.randomUUID().replace(/-/g, "").slice(0, 16);
}

// Detecta o tipo de dispositivo
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
}

// Detecta o navegador
function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("SamsungBrowser")) return "Samsung Browser";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  if (ua.includes("Trident")) return "IE";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Unknown";
}

// Detecta o OS
function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown";
}

// Extrai UTMs da URL
function getUTMs(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
  };
}

// Capture Google Click IDs from URL
function getGclid(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("gclid") || params.get("gbraid") || params.get("wbraid") || "";
}

// Envia dados para a edge function
async function sendAnalytics(type: string, data: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/track-analytics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, data }),
    });
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

export function useAnalytics() {
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const pageviewIdRef = useRef<string | null>(null);
  const pageEnteredAtRef = useRef<number>(Date.now());
  const maxScrollDepthRef = useRef<number>(0);
  const isInitializedRef = useRef<boolean>(false);

  // Inicializa sessão
  const initSession = useCallback(async () => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Verifica/cria visitor_id
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateVisitorId();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    // Verifica sessão existente (válida por 30 min de inatividade)
    const existingSessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (existingSessionId) {
      sessionIdRef.current = existingSessionId;
      return;
    }

    // Cria nova sessão
    const utms = getUTMs();
    // Grab the event_id generated by Meta Pixel for CAPI deduplication
    const metaEventId = (window as any).__meta_pageview_event_id || undefined;
    const gclid = getGclid();
    const result = await sendAnalytics("session", {
      visitor_id: visitorId,
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      referrer: document.referrer || null,
      meta_event_id: metaEventId,
      page_url: window.location.href,
      ...(gclid ? { gclid } : {}),
      ...utms,
    });

    if (result?.session_id) {
      sessionIdRef.current = result.session_id as string;
      sessionStorage.setItem(SESSION_ID_KEY, result.session_id as string);
    }
  }, []);

  // Registra pageview
  const trackPageview = useCallback(async () => {
    if (!sessionIdRef.current) return;

    // Finaliza pageview anterior
    if (pageviewIdRef.current) {
      const duration = Math.floor((Date.now() - pageEnteredAtRef.current) / 1000);
      await sendAnalytics("update_pageview", {
        pageview_id: pageviewIdRef.current,
        duration_seconds: duration,
        scroll_depth: maxScrollDepthRef.current,
      });
    }

    // Reseta contadores
    pageEnteredAtRef.current = Date.now();
    maxScrollDepthRef.current = 0;

    // Registra nova pageview
    const result = await sendAnalytics("pageview", {
      session_id: sessionIdRef.current,
      page_path: window.location.pathname,
      page_title: document.title,
    });

    if (result?.pageview_id) {
      pageviewIdRef.current = result.pageview_id as string;
      sessionStorage.setItem(PAGEVIEW_ID_KEY, result.pageview_id as string);
    }
  }, []);

  // Rastreia evento
  const trackEvent = useCallback(async (eventType: string, eventName: string, elementText?: string, metadata?: Record<string, unknown>) => {
    if (!sessionIdRef.current) return;

    await sendAnalytics("event", {
      session_id: sessionIdRef.current,
      event_type: eventType,
      event_name: eventName,
      element_text: elementText,
      page_path: window.location.pathname,
      metadata,
    });
  }, []);

  // Delay analytics initialization until after LCP to avoid blocking critical rendering path
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      initSession().then(() => {
        trackPageview();
      });
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [initSession, trackPageview]);

  // Rastreia mudanças de rota
  useEffect(() => {
    if (isInitializedRef.current && sessionIdRef.current) {
      trackPageview();
    }
  }, [location.pathname, trackPageview]);

  // Rastreia scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) {
        maxScrollDepthRef.current = 100;
        return;
      }
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(scrollPercent, 100));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Rastreia cliques em elementos com data-track
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const trackElement = target.closest("[data-track]") as HTMLElement | null;
      
      if (trackElement) {
        const eventName = trackElement.getAttribute("data-track") || "unknown";
        const elementText = trackElement.textContent?.slice(0, 100) || "";
        trackEvent("click", eventName, elementText);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [trackEvent]);

  // Finaliza pageview ao sair
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pageviewIdRef.current) {
        const duration = Math.floor((Date.now() - pageEnteredAtRef.current) / 1000);
        // Use sendBeacon for reliability on page unload
        navigator.sendBeacon(
          `${SUPABASE_URL}/functions/v1/track-analytics`,
          JSON.stringify({
            type: "update_pageview",
            data: {
              pageview_id: pageviewIdRef.current,
              duration_seconds: duration,
              scroll_depth: maxScrollDepthRef.current,
            },
          })
        );
      }

      if (sessionIdRef.current) {
        navigator.sendBeacon(
          `${SUPABASE_URL}/functions/v1/track-analytics`,
          JSON.stringify({
            type: "end_session",
            data: { session_id: sessionIdRef.current },
          })
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return { trackEvent };
}
